const SearchStorage = {
  /**
   * Generates a unique localStorage key for a given search ID.
   * @param {string} id - The identifier for the search context.
   * @returns {string} The generated localStorage key.
   */
  key(id) {
    return `SearchBarSaved.${id}`;
  },

  /**
   * Retrieves the array of saved search strings for a given ID.
   * @param {string} id - The identifier for the search context.
   * @returns {string[]} An array of saved search strings.
   */
  get(id) {
    const raw = localStorage.getItem(this.key(id));
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  /**
   * Checks if a specific search string is saved for the given ID.
   * @param {string} id - The identifier for the search context.
   * @param {string} searchString - The search string to check.
   * @returns {boolean} True if the search string exists, false otherwise.
   */
  has(id, searchString) {
    const searches = this.get(id);
    return searches.includes(searchString);
  },

  /**
   * Adds a new search string to the saved searches for the given ID.
   * If the search string already exists, it will not be added again.
   * @param {string} id - The identifier for the search context.
   * @param {string} searchString - The search string to add.
   */
  add(id, searchString) {
    const searches = this.get(id);
    if (!searches.includes(searchString)) {
      searches.push(searchString);
      localStorage.setItem(this.key(id), JSON.stringify(searches));
    }
  },

  /**
   * Removes a specific search string from the saved searches for the given ID.
   * @param {string} id - The identifier for the search context.
   * @param {string} searchString - The search string to remove.
   */
  remove(id, searchString) {
    const searches = this.get(id).filter(s => s !== searchString);
    localStorage.setItem(this.key(id), JSON.stringify(searches));
  },

  /**
   * Clears all saved search strings for the given ID.
   * @param {string} id - The identifier for the search context.
   */
  clear(id) {
    localStorage.removeItem(this.key(id));
  },

  /**
   * Returns the number of saved search strings for the given ID.
   * @param {string} id - The identifier for the search context.
   * @returns {number} The count of saved search strings.
   */
  count(id) {
    const searches = this.get(id);
    return searches.length;
  }
};

export default SearchStorage;